import { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg, { getWebPageSchema, getBreadcrumbSchema } from '@/components/feature/SchemaOrg';
import Breadcrumb, { getBreadcrumbForCategory } from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const category = categories.find((c) => c.slug === slug);
  const filteredArticles = category
    ? articles.filter((a) => a.categorySlug === category.slug)
    : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-2">分类不存在</h2>
            <p className="text-sm text-foreground-500 mb-4">找不到该分类页面</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg whitespace-nowrap">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = `${category.name} - 开云体育`;
  const pageDesc = `${category.description}。开云体育为您提供${category.name}相关的最新内容和专业指南。`;
  const pageUrl = `https://kaixinhappy.cn/category/${category.slug}`;
  const breadcrumbItems = getBreadcrumbForCategory(category.slug);
  const breadcrumbSchemaItems = breadcrumbItems.map((b) => ({ name: b.label, url: b.href ? `https://kaixinhappy.cn${b.href}` : undefined }));

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`开云体育${category.name},开云体育网址,${category.name}指南`}
        canonicalUrl={pageUrl}
        ogTitle={pageTitle}
        ogDescription={pageDesc}
        ogUrl={pageUrl}
      />
      <SchemaOrg {...getWebPageSchema(pageTitle, pageDesc, pageUrl)} />
      <SchemaOrg {...getBreadcrumbSchema(breadcrumbSchemaItems)} />

      <Navbar />

      <main className="pt-16">
        {/* Category Hero */}
        <section className="relative h-[200px] md:h-[260px] flex items-center">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20sports%20stadium%20architecture%20with%20warm%20ambient%20lighting%2C%20clean%20geometric%20lines%2C%20minimalist%20design%2C%20abstract%20sports%20theme%2C%20golden%20and%20amber%20color%20palette%2C%20professional%20atmosphere%2C%20wide%20panoramic%20view&width=1600&height=520&seq=category-hero-2026&orientation=landscape"
              alt={category.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/45 to-black/25"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto w-full px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mt-3">{category.name}</h1>
            <p className="text-sm text-background-50/80 mt-2">{category.description}</p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200/60 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 py-3 overflow-x-auto flex-nowrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => navigate(`/category/${cat.slug}`)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === category.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded">{article.category}</span>
                        <span className="text-xs text-foreground-400">{article.date}</span>
                      </div>
                      <h2 className="font-heading font-semibold text-foreground-900 text-sm leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-xs text-foreground-500 mt-2 leading-relaxed line-clamp-2">{article.excerpt}</p>
                      <div className="flex items-center gap-1.5 mt-4 flex-wrap">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-background-100 text-foreground-500 text-xs rounded">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
                <Link to="/" className="inline-flex items-center gap-2 mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium">
                  <i className="ri-arrow-left-line"></i> 返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}